import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/homepage/Navbar";
import HeroSection from "@/components/homepage/HeroSection";
import ServicesSection from "@/components/homepage/ServicesSection";
import WhyChooseUsSection from "@/components/homepage/WhyChooseUsSection";
import AITasteBuilderSection from "@/components/AITasteBuilderSection";
import TeamSection from "@/components/TeamSection";
import ContactSection from "@/components/homepage/ContactSection";
import Footer from "@/components/homepage/Footer";
import FloatingCTA from "@/components/FloatingCTA";

interface HomepageSettings {
  show_team_section: boolean;
}

const Index = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Clear previous inquiry data when landing on homepage (unless returning mid-flow)
    // IMPORTANT: We don't create a new session ID because the Supabase client
    // is already initialized with the existing session ID in its headers.
    // Creating a new session ID would cause RLS policy violations.
    const existingSessionId = sessionStorage.getItem('session_id');
    const isReturningFromFlow = sessionStorage.getItem('inquiry_in_progress');
    
    if (existingSessionId && !isReturningFromFlow) {
      // User is starting fresh - clear old favorite songs but keep same session ID
      sessionStorage.removeItem('inquiry_in_progress');
      
      // Clear favorite songs for this session to start fresh
      supabase
        .from('favorite_songs')
        .delete()
        .eq('session_id', existingSessionId)
        .then(({ error }) => {
          if (error) {
            console.error('Error clearing old songs:', error);
          } else {
            console.log('Cleared old session songs for fresh inquiry');
          }
        });
      
      // Also clear contact form drafts
      supabase
        .from('contact_form_drafts')
        .delete()
        .eq('session_id', existingSessionId)
        .then(({ error }) => {
          if (error) {
            console.error('Error clearing old drafts:', error);
          } else {
            console.log('Cleared old drafts for fresh inquiry');
          }
        });
    }
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsAuthenticated(!!session);
    });
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setIsAuthenticated(!!session);
    });
    
    return () => subscription.unsubscribe();
  }, []);

  // Fetch homepage settings
  const { data: homepageSettings } = useQuery({
    queryKey: ["site-settings", "homepage"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("value")
        .eq("id", "homepage")
        .single();

      if (error) return { show_team_section: true };
      return data.value as unknown as HomepageSettings;
    },
    staleTime: 1000 * 60 * 5,
  });

  return (
    <div className="min-h-screen bg-background">
      <Navbar isAuthenticated={isAuthenticated} />
      
      {/* Hero - Primary conversion point */}
      <HeroSection />
      
      {/* Services - Build desire */}
      <ServicesSection />
      
      {/* Why Choose Us - Handle objections */}
      <WhyChooseUsSection />
      
      {/* AI Section - Differentiation + conversion */}
      <AITasteBuilderSection />
      
      {/* Team Section - Conditionally Rendered */}
      {homepageSettings?.show_team_section !== false && <TeamSection />}
      
      {/* Contact Form - Final conversion */}
      <ContactSection />
      
      <Footer />
      
      {/* Floating CTA - Always accessible conversion */}
      <FloatingCTA />
    </div>
  );
};

export default Index;
